import { TransactionJsonType, TransactionType } from "./enums/TransactionType";
import { IssuedBy } from "./const/Transactions";
import { FileUploadConfig, UploadedAttachment } from "../fileUpload/fileUploadDomain";
import { IncreaseType } from "../shareholderRegister/transactions/issue/issueTransactionDomain";
import { SettlementMethods } from "../shareholderRegister/transactions/groupContribution/groupContributionDomain";
import { DistributionMode } from "../shareholderRegister/transactions/dividend/dividendDomain";
import { Purpose } from "../shareholderRegister/transactions/commonCapitalReduction";

import { SharenumberInterval } from "../shareholderRegister/shareholderRegisterDomain";
import { Amount, LocalizedText } from "../common/common";
import { ShareTypeCode } from "../common/share";

export interface TransactionDetails {
  buyerBrokerFee: number;
  buyerOtherCosts: number;
  currency: string;
  numberOfShares: number;
  numberOfSharesAfter?: number;
  numberOfSharesBefore?: number;
  shareNominalValueBefore?: number;
  shareNominalValueAfter?: number;
  pricePerShare: number;
  sellerBrokerFee: number;
  sellerOtherCosts: number;
  shareTypeId: number;
  shareTypeCode?: ShareTypeCode;
  shareholderBuyerId?: number;
  shareholderBuyerName?: string;
  shareholderSellerId?: number;
  shareholderSellerName?: string;
  totalPrice: number;
  transactionType: keyof typeof TransactionType;
  jsonData: string;
  dividendPaidAmount?: number;
}

export interface Transaction {
  comment?: string;
  nameFrom?: string;
  nameTo?: string;
  numberOfSharesBefore: NumberOfSharesPerShareType[];
  descriptions: LocalizedText[];
  organizationnumber: string;
  transactionDate: string;
  transactionDetails: TransactionDetails[];
  transactionlogId: number;
  updatedDate: string;
  transactionType: keyof typeof TransactionType;
  userName: string;
  fileUploadConfig?: FileUploadConfig;
  attachments: UploadedAttachment[];
  payload?: unknown;
  projectId?: string;
}

export interface NumberOfSharesPerShareType {
  shareTypeId: number;
  numberOfShares: number;
}

export interface BuyDetails {
  buyerShareholderId: number;
  shareTypeId: number;
  numberOfShares: number;
  sellingPrice: number | null;
  currency: string;
  buyerBrokerFee?: number;
  buyerOtherCosts?: number;
  sellerBrokerFee?: number;
  sellerOtherCosts?: number;
  taxContinuity: boolean;
}

export interface NewTransaction {
  organizationnumber: string;
  sellerShareholderId: number;
  saleDetails: BuyDetails[];
  comment?: string;
  transactionDate: string;
  transactionType:
    | TransactionType.BUY_SELL
    | TransactionType.GIFT
    | TransactionType.INHERITANCE
    | TransactionType.OTHER_TRANSFER;
}

export interface TransactionAllShares {
  organizationnumber: string;
  transactionDate: string;
  buyerShareholderId: number;
  brokerFee?: number;
  otherCosts?: number;
  sellerOtherCosts?: number;
  comment?: string;
  includeCompanyOwnShares: boolean;
  sharetypeSellingPrices: Array<{
    sellingPrice: number;
    currency: string;
    sharetypeId: number;
    sellerBrokerFee?: number;
    buyerBrokerFee?: number;
  }>;
}

export interface ShareSplitTransaction {
  organizationnumber: string;
  splitRatio: number;
  decisionDate: string;
}

export interface ShareTypeAdjustment {
  sharetypeId: number;
  currentNumberOfShares: number;
  adjustedToNumberOfShares: number;
}

export interface FractionShareAdjustment {
  shareholderId: number;
  sharetypesAdjustment?: Array<ShareTypeAdjustment>;
}

export interface ShareReverseSplit {
  organizationnumber: string;
  reverseSplitRatio: number;
  fractionShareAdjustments: Array<FractionShareAdjustment>;
  decisionDate: string;
}

export interface ShareholderSharetypeSpecification {
  shareTypeId: number | "";
  numberOfShares: number | "";
  amountToBePaidInExcessOfReductionShareCapital: Record<number, Amount> | null; // Map<Long, Amount> = null
}

export interface ShareholderCancellationSpecification {
  shareholderId: number | "";
  shareholderSharetypeSpecifications?: Array<ShareholderSharetypeSpecification>;
}

export interface CancellationOfShares {
  organizationnumber: string;
  shareholderSharetypeSpecifications: Array<ShareholderSharetypeSpecification>;
  shareReductionPurpose: Purpose | "";
  isTreatedAsRepayment: boolean;
  shareholderCancellationSpecifications?: Array<ShareholderCancellationSpecification>;
  resolutionDate?: string;
  isRegisteredInNorwegianBusinessEnterprise: boolean;
  registrationInNorwegianRegisterDate?: string | null;
  isCancellationProRata: boolean;
}

export interface ReductionOfNominalValue {
  organizationnumber: string;
  shareReductionPurpose: Purpose | "";
  isTreatedAsRepayment: boolean;
  reduceInNominalValuePerShare: string;
  amountToBePaidInExcessOfReductionShareCapital: Record<number, Amount> | null;
  resolutionDate?: string;
  isRegisteredInNorwegianBusinessEnterprise: boolean;
  registrationInNorwegianRegisterDate?: string | null;
}

export interface PerSharetypePayment {
  sharetypeId: number;
  totalAmountForShareType: Amount;
}

export interface PayDividendRecipients {
  shareholderId: number;
  perSharetypePayment: PerSharetypePayment[];
}

export interface DividendPayment {
  sharetypeId: number;
  paymentMethod: string;
  amount: Amount;
  excludeCompanysOwnShares: boolean;
}
export interface PayDividendPayment {
  dividendPayment: DividendPayment[];
  evenDistributionRecipientsAtDecisionTime: boolean;
  evenDistributionRecipientsAtSpecificTime?: string | null;
}

export interface PayDividend {
  organizationnumber: string;
  resolutionBy: Resolution | "";
  decisionDate: string;
  dividendDistributionMode: DistributionMode | "";
  evenDividendPayment?: PayDividendPayment;
  perShareholderDividendPayment?: PayDividendRecipients[];
  resolutionIsBasedOnIntermediateBalanceSheet: boolean;
  repaimentOfPaidInCapital: boolean;
}

export interface GroupContributionCompany {
  organizationnumber: string;
  companyName: string;
  settlementMethod: SettlementMethods | "";
  amount: Amount;
}

export interface RegisterGroupContribution {
  organizationnumber: string;
  resolutionBy: Resolution | "";
  isResolutionBasedOnIntermediateBalanceSheet: boolean;
  hasBalanceBeenAudited?: boolean;
  balanceSheetDate?: string;
  decisionDate: string;
  groupContributionCompanies: GroupContributionCompany[];
}

export enum Resolution {
  POA = "PoA",
  GENERAL_MEETING = "generalMeeting",
}

export enum PaymentMethods {
  PER_SHARE = "perShare",
  TOTAL_INCL = "totalAmountIncl",
  TOTAL_EX = "totalAmountEx",
}

export enum GroupMethod {
  ESTABLISH_CLAIM = "ESTABLISH_CLAIM",
  SET_OFF_OTHER_BALANCE = "SET_OFF_OTHER_BALANCE",
  CASH_PAYMENT = "CASH_PAYMENT",
  OTHER_METHOD = "OTHER_METHOD",
}

export interface SharePriceChangeDTO {
  shareId: number;
  previousValue: number;
  newValue: number;
}
export interface IssueShareTypeDTO {
  sharetypeId: number;
  numberOfShares: number;
  shareholderId: number;
  sharePrice: number;
  currency: string;
}

export interface AmountPaidInPerShareTypeDTO {
  sharetypeId: number;
  sharePrice: number;
}

export type IssueNewSharesDTO = {
  type: TransactionJsonType.ISSUE;
  data: {
    organizationnumber: string;
    registrationDate?: string;
    shareholderRightsDate?: string;
    shareAllocation: IssueShareTypeDTO[];
    isBonusIssue?: boolean;
    transactionType: TransactionType.ISSUE;
    issuedBy?: IssuedBy;
    boardMeetingPoDDate?: string | null;
    generalMeetingDate?: string | null;
    registeredInNorwegian?: boolean;
    incorporationDate?: string;
    increaseType?: IncreaseType;
    shareNominalValue?: number;
    isPending?: boolean;
  };
};

export type IncreaseNominalValueDTO = {
  type: TransactionJsonType.INCREASE_NOMINAL_VALUE;
  data: {
    organizationnumber: string;
    registrationDate?: string;
    isBonusIssue: boolean;
    issuedBy?: IssuedBy;
    boardMeetingPoDDate?: string | null;
    generalMeetingDate?: string | null;
    amountPaidInPerShare: AmountPaidInPerShareTypeDTO[];
    increaseInNominalValuePerShare: number;
    increaseType?: IncreaseType;
    isPending?: boolean;
  };
};

export type ShareSplitDTO = {
  type: TransactionJsonType.SPLIT;
  data: {
    organizationnumber: string;
    splitRatio: number;
    comment?: string | null;
  };
};

export type ShareReverseSplitDTO = {
  type: TransactionJsonType.REVERSE_SPLIT;
  data: {
    organizationnumber: string;
    reverseSplitRatio: number;
    fractionShareAdjustments: FractionShareAdjustment[];
  };
};

export type ReductionOfNominalValueDTO = {
  type: TransactionJsonType.REDUCTION_NOMINAL_VALUE;
  data: {
    organizationnumber: string;
    shareReductionPurpose: Purpose;
    reduceInNominalValuePerShare: string;
    amountToBePaidInExcessOfReductionShareCapital?: Record<string, Amount> | null;
    resolutionDate?: string | null;
    isRegisteredInNorwegianBusinessEnterprise: boolean;
    registrationInNorwegianRegisterDate?: string | null;
  };
};

export type CancellationOfSharesDTO = {
  type: TransactionJsonType.CANCELLATION_OF_SHARES;
  data: {
    organizationnumber: string;
    shareholderSharetypeSpecifications: {
      shareTypeId: number;
      numberOfShares: number;
      amountToBePaidInExcessOfReductionShareCapital?: Record<string, Amount>;
    }[];
    shareReductionPurpose: Purpose;
    shareholderCancellationSpecifications: {
      shareholderId: number;
      shareholderSharetypeSpecifications: {
        shareTypeId: number;
        numberOfShares: number;
        amountToBePaidInExcessOfReductionShareCapital?: Record<string, Amount>;
      }[];
    }[];
    resolutionDate: string;
    isRegisteredInNorwegianBusinessEnterprise: boolean;
    registrationInNorwegianRegisterDate?: string | null;
  };
};

export type PayDividendJsonData = {
  type: TransactionJsonType.PAY_DIVIDEND;
  data: {
    organizationnumber: string;
    resolutionBy: Resolution;
    resolutionIsBasedOnIntermediateBalanceSheet: boolean;
    repaimentOfPaidInCapital: boolean;
    evenDividendPayment?: PayDividendPayment | null;
    perShareholderDividendPayment: PayDividendRecipients[];
    sharePriceChanges: SharePriceChangeDTO[];
  };
};

export type GroupContributionJsonData = {
  type: TransactionJsonType.GROUP_CONTRIBUTION;
  data: {
    organizationnumber: string;
    companyName: string;
    settlementMethod: GroupMethod;
    amount: Amount;
    resolutionBy: Resolution;
    isResolutionBasedOnIntermediateBalanceSheet: boolean;
    hasBalanceBeenAudited?: boolean;
    decisionDate: string;
    balanceSheetDate?: string | null;
  };
};

export interface SharesToConvertForShareholder {
  shareholderId: number;
  numberOfShares: number;
}

export type ConversionOfSharesDTO = {
  type: TransactionJsonType.CONVERSION_OF_SHARES;
  data: {
    organizationnumber: string;
    fromShareTypeId: number;
    toShareTypeId: number;
    transactionDate: string;
    convertAllShares: boolean;
    sharesToConvertPerShareholder: SharesToConvertForShareholder[];
  };
};

export type ConversionFromISINDTO = {
  type: TransactionJsonType.CONVERSION_FROM_ISIN;
  data: {
    organizationnumber: string;
    isinNumber: string;
    toShareTypeId: number;
    transactionDate: string;
  };
};

export type LiquidationDTO = {
  type: TransactionJsonType.LIQUIDATION;
  data: {
    organizationnumber: string;
    transactionDate: string;
  };
};

export type MergerDTO = {
  type: TransactionJsonType.MERGER;
  data: {
    mergerType: MergerType;
    organizationnumber: string;
    transactionDate: string;
    acquiror: NamedOrganisationNumber;
    target: NamedOrganisationNumber;
    mergerCapitalChanges: Array<MergerCapitalChange>;
    targetShareClassData: Array<TargetShareClassData>;
    acquirorNominalValue?: number;
    targetNominalValue?: number;
    shareAllocation: Array<MergerShareAllocation>;
  };
};

export type MergerCapitalChange = {
  targetShareTypeCode: ShareTypeCode;
  acquirorShareTypeId: number;
  paidInCapitalForTarget: number;
  paidInPremiumForTarget: number;
  targetNumberOfShares?: number;
  issuedNumberOfShares?: number;
};

export type TargetShareClassData = {
  targetShareTypeId: number;
  acquirorShareTypeCode: ShareTypeCode;
  acquirorNumberOfSharesInShareType: number;
};

export type MergerShareAllocation = {
  shareholderId: number;
  shareTypeId: number;
  numberOfShares: number;
  targetShareTypeCode: ShareTypeCode;
};

export type NamedOrganisationNumber = {
  name: string;
  orgNo: string;
};

export enum MergerType {
  ORDINARY_ACQUISITION = "ORDINARY_ACQUISITION",
  PARENT_WHOLLY_OWNED_SUBSIDARY = "PARENT_WHOLLY_OWNED_SUBSIDARY",
  COMPANIES_WITH_SAME_OWNER = "COMPANIES_WITH_SAME_OWNER",
  REVERSE_PARENT_SUBSIDIARY = "REVERSE_PARENT_SUBSIDIARY",
}

export type ShareholdingAllocation = {
  sharetypeId: number;
  numberOfShares: number;
  shareholderId: number;
  sharenumberIntervals?: SharenumberInterval[];
  entryDate?: string;
};

export type ShareholdingPerDateDTO = {
  type: TransactionJsonType.SHAREHOLDING_PER_DATE;
  data: {
    organizationnumber: string;
    shareAllocation: ShareholdingAllocation[];
    transactionDate: string;
    nominalValue: number;
  };
};

export type JsonData =
  | IssueNewSharesDTO
  | IncreaseNominalValueDTO
  | ShareSplitDTO
  | ShareReverseSplitDTO
  | MergerDTO
  | ReductionOfNominalValueDTO
  | CancellationOfSharesDTO
  | PayDividendJsonData
  | GroupContributionJsonData
  | ConversionFromISINDTO
  | ConversionOfSharesDTO;

export type ContractNoteRequest = {
  transactionLogId: number;
  shareholderBuyerId: number;
  shareholderSellerId: number;
  fileName: string;
};
